import { Auth } from 'aws-amplify';
import { ConfigurationManager } from 'modules/shell/utils/config';
import { createHttpClient } from 'utils';
import { logoutUrl } from 'constants/index';
import MockApiService from './MockApiService';
import { store } from '../store';
import appEventBus, {
  API_ADD_ACTIVE_AD_GROUP_HEADER,
  API_REMOVE_ACTIVE_AD_GROUP_HEADER,
  API_ADD_STORE_MAPPING_HEADER,
} from './appEventBus';

let config = new ConfigurationManager();
let logger = { error: () => null };

const loggerResolver = () => logger;

const httpClient = createHttpClient(store, loggerResolver);
const globalHttpHeaders = new Map();

let mockApiService;

export const cancelRequest = { cancel: () => null };

if (process.env.NODE_ENV === 'development') {
  mockApiService = MockApiService.createMockApiService();
}

/**
 *  Create API service object with next interface
 *  - get (uri, options): Promise
 *  - post (uri, data, options): Promise
 *  - put (uri, data, options): Promise
 *  - delete (uri, options): Promise
 *
 *  options - represents next data:
 *  - headers - key/value http headers
 *  - params - URL query parameters
 *  - clientId - shortcut for `headers: { 'X-Customer-Id': <value> }`
 *  - httpClientOptions - options specific to httpClient instance
 *
 * @param {Function} endpointResolver - function which should return endpoint base URL
 * @returns {{ get: function(...*): Promise<any>, post: function(...*): Promise<any>, put: function(...*): Promise<any>, delete: function(...*): Promise<any> }}
 */
const createApiService = (endpointResolver, excludeAuthorization = false, excludeStoreMapping = false) => {
  return ['get', 'post', 'put', 'delete'].reduce((res, method) => {
    res[method] = async (uri, ...args) => {
      // use mock service in case it's available and enabled
      if (mockApiService && mockApiService.isMockAvailableAndEnabled(method, endpointResolver(), uri)) {
        const [options = {}] = args;
        const requestPayload = ['post', 'put'].includes(method) ? { body: options } : { params: options.params };

        return mockApiService.exec(method, endpointResolver(), uri, requestPayload);
      }
      let bearerToken;
      try {
        // Get auth JWT token
        bearerToken = (await Auth.currentSession()).getIdToken().getJwtToken();
      } catch (e) {
        if (e.message === 'Refresh Token has expired' || e === 'No current user') {
          window.location.href = logoutUrl(config.get('Auth'));
          return;
        }
        logger.error(e);
        throw e;
      }

      const requestArgs = [uri];
      let postBody;
      let options;

      if (['post', 'put'].includes(method)) {
        [postBody = null, options = {}] = args;
        requestArgs.push(postBody);
      } else {
        [options = {}] = args;
      }

      const { headers, params, httpClientOptions, clientId, data, cancelable } = options;

      // build HTTP headers
      const preparedHeaders = {
        ...(!excludeAuthorization && { Authorization: `Bearer ${bearerToken}` }),
        ...(clientId && { 'X-Customer-Id': clientId }),
        ...headers,
      };

      if (globalHttpHeaders.size > 0) {
        globalHttpHeaders.forEach((headerValue, headerName) => {
          if (headerName === 'X-NMG-Facility' && (excludeAuthorization || excludeStoreMapping)) {
            return;
          }
          preparedHeaders[headerName] = headerValue;
        });
      }

      const requestOptions = {
        baseURL: endpointResolver(),
        headers: preparedHeaders,
        cancelToken:
          cancelable &&
          new httpClient.CancelToken((c) => {
            cancelRequest.cancel = c;
          }),
        params,
        data,
      };

      requestArgs.push(requestOptions);

      return httpClient(httpClientOptions)[method](...requestArgs);
    };

    return res;
  }, {});
};

// TODO: Should be found much better solution instead of just exporting function below
export const updateApiConfigAndLogger = (newConfig, newLogger, configMetaData) => {
  config = newConfig;
  logger = newLogger;

  // add a log record about configuration object used for app initialization
  if (configMetaData.status === 200) {
    logger.info(configMetaData.statusText, configMetaData);
  } else {
    logger.warn(configMetaData.statusText, configMetaData);
  }

  if (mockApiService) {
    mockApiService.buildMockRouting(config);
  }
};
// TODO: End

appEventBus.on(API_ADD_ACTIVE_AD_GROUP_HEADER, (value) => globalHttpHeaders.set('X-NMG-Group', value));
appEventBus.on(API_REMOVE_ACTIVE_AD_GROUP_HEADER, () => globalHttpHeaders.delete('X-NMG-Group'));
appEventBus.on(API_ADD_STORE_MAPPING_HEADER, (value) => globalHttpHeaders.set('X-NMG-Facility', value));

export const clientsService = createApiService(() => config.get('api.endpoints.clients'));
export const clientsCentralSearchService = createApiService(() => config.get('api.endpoints.clientsCentralSearch'));
export const clientsCentralSearchServiceV2 = createApiService(() => config.get('api.endpoints.clientsCentralSearchV2'));
export const profileService = createApiService(() => config.get('api.endpoints.profile'));
export const cmdProfileService = createApiService(() => config.get('api.endpoints.cmdProfile'));
export const cmdProfileServiceV3 = createApiService(() => config.get('api.endpoints.cmdProfileV3'));
export const cmdProfileServiceV4 = createApiService(() => config.get('api.endpoints.cmdProfileV4'));
export const nmProfilePaymentUCAService = createApiService(() => config.get('api.endpoints.nmProfilePaymentUCA'));
export const nmProfileAddressUCAService = createApiService(() => config.get('api.endpoints.nmProfileAddressUCA'));
export const bgProfilePaymentUCAService = createApiService(() => config.get('api.endpoints.bgProfilePaymentUCA'));
export const bgProfileAddressUCAService = createApiService(() => config.get('api.endpoints.bgProfileAddressUCA'));
export const nmProfileAttributesUCAService = createApiService(() => config.get('api.endpoints.nmProfileAttributesUCA'));
export const bgProfileAttributesUCAService = createApiService(() => config.get('api.endpoints.bgProfileAttributesUCA'));
export const wishlistService = createApiService(() => config.get('api.endpoints.wishlist'));
export const cartService = createApiService(() => config.get('api.endpoints.cart'));
export const cartServiceV2 = createApiService(() => config.get('api.endpoints.cartV2'));
export const paymentService = createApiService(() => config.get('api.endpoints.paymentIframe'));
export const paymentServiceV2 = createApiService(() => config.get('api.endpoints.paymentIframeV2'));
export const orderService = createApiService(() => config.get('api.endpoints.order'));
export const orderServiceV2 = createApiService(() => config.get('api.endpoints.orderV2'));
export const orderServiceV3 = createApiService(() => config.get('api.endpoints.orderV3'));
export const orderServiceV4 = createApiService(() => config.get('api.endpoints.orderV4'));
export const orderHistoryService = createApiService(() => config.get('api.endpoints.orderHistory'));
export const associateHistoryService = createApiService(() => config.get('api.endpoints.associateOrderHistory'));
export const associatePerformanceService = createApiService(() => config.get('api.endpoints.associatePerformance'));
export const associateScorecardService = createApiService(() => config.get('api.endpoints.associateScorecard'));
export const groupsService = createApiService(() => config.get('api.endpoints.groups'));
export const groupsV2Service = createApiService(() => config.get('api.endpoints.groupsV2'));
export const groupsV3Service = createApiService(() => config.get('api.endpoints.groupsV3'));
export const conversationService = createApiService(() => config.get('api.endpoints.conversations'));
export const conversationServiceV2 = createApiService(() => config.get('api.endpoints.conversationsV2'));
export const attachmentsService = createApiService(() => config.get('api.endpoints.getUploadUrls'));
export const attachmentsServiceV2 = createApiService(() => config.get('cloudinary.url'), true);
export const signAttachmentsService = createApiService(() => config.get('api.endpoints.signFileUpload'));
export const eventDetailsService = createApiService(() => config.get('api.endpoints.eventDetails'));
export const productServiceWithInHouseData = createApiService(() => config.get('api.endpoints.productFromInHouseData'));
export const productServiceWithInHouseDataV4 = createApiService(() =>
  config.get('api.endpoints.productFromInHouseDataV4'),
);
export const productDetailsService = createApiService(() => config.get('api.endpoints.productDetails'));
export const farfetchProductDetailsService = createApiService(() => config.get('api.endpoints.farfetchProductDetails'));
export const productCatalogService = createApiService(() => config.get('api.endpoints.productCatalog'));
export const notifyMeService = createApiService(() => config.get('api.endpoints.notifyMe'));
export const voiceCallService = createApiService(() => config.get('twilio.tokens.voiceCall'));
export const storeService = createApiService(() => config.get('api.endpoints.storeInfo'), false, true);
export const paginatedOrderHistoryService = createApiService(() => config.get('api.endpoints.paginatedOrderHistory'));
export const skuTrackerService = createApiService(() => config.get('api.endpoints.skuTrackers'));
export const marketingAssetsService = createApiService(() => config.get('api.endpoints.marketingAssets'));
export const marketingScriptsService = createApiService(() => config.get('api.endpoints.marketingScripts'));
export const emailTemplatesService = createApiService(() => config.get('api.endpoints.emailTemplates'));
export const emailSignatureService = createApiService(() => config.get('api.endpoints.emailSignature'));
export const shortenedProductUrlService = createApiService(() => config.get('urlShortener.url'));
export const remindersService = createApiService(() => config.get('api.endpoints.reminders'));
export const alterationsService = createApiService(() => config.get('api.endpoints.alterations'));
export const managerAlterationsService = createApiService(() => config.get('api.endpoints.managerAlterations'));
export const clientConnectsService = createApiService(() => config.get('api.endpoints.clientConnects'));
export const stylistManagerClientConnectsService = createApiService(() =>
  config.get('api.endpoints.stylistManagerClientConnects'),
);
export const globalOrdersSearchService = createApiService(() => config.get('api.endpoints.globalOrdersSearch'));
export const globalOrdersSearchV2Service = createApiService(() => config.get('api.endpoints.globalOrdersSearchV2'));
export const chatService = createApiService(() => config.get('api.endpoints.chat'));
export const lookPageSvc = createApiService(() => config.get('api.endpoints.generateLooksPreview'));
export const memosService = createApiService(() => config.get('api.endpoints.memos'));
export const reservationsService = createApiService(() => config.get('api.endpoints.reservations'));
export const reservationSubmitPosHoldsService = createApiService(() =>
  config.get('api.endpoints.reservationSubmitPosHolds'),
);
export const clientInsightService = createApiService(() => config.get('api.endpoints.clientInsights'));
export const overrideService = createApiService(() => config.get('api.endpoints.takesOverride'));
export const accountAssociateHierarchyService = createApiService(() =>
  config.get('api.endpoints.accountAssociateHierarchy'),
);
export const attributionService = createApiService(() => config.get('api.endpoints.attributionService'));
export const storeSendsReceiptService = createApiService(() => config.get('api.endpoints.storeSendsReceiptService'));
export const getProfileAttributesUcaService = (isBgAssociate) =>
  isBgAssociate ? bgProfileAttributesUCAService : nmProfileAttributesUCAService;
export const associateAssignmentService = createApiService(() =>
  config.get('api.endpoints.associateAssignmentService'),
);
export const auditService = createApiService(() => config.get('api.endpoints.auditService'));
export const auditSignedUrlService = createApiService(() => config.get('api.endpoints.auditSignedUrlService'));
export const auditOrderLists = createApiService(() => config.get('api.endpoints.auditOrderLists'));
export const editStyleboards = createApiService(() => config.get('api.endpoints.editStyleboards'));
export const notificationsService = createApiService(() => config.get('api.endpoints.notifications'));
export const styleboardsService = createApiService(() => config.get('api.endpoints.styleboards'));
export const paymentServiceV3 = createApiService(() => config.get('api.endpoints.paymentService'));
export const inventoryStatusService = createApiService(() => config.get('api.endpoints.farfetchInventory'));
export const productCatalogFarfetchService = createApiService(() => config.get('api.endpoints.productCatalogFarfetch'));
